import { routePaths } from 'components/Routes/data';
import permissions from 'constants/permissions';
import { BulkUploadTypes, UploadTypeConfig, UploadTypeConfigCallback } from 'constants/bulkUpload';
import { format } from 'date-fns';

type PreviewColumns = NonNullable<UploadTypeConfig['preview']['columns']>;
type PreviewItem = Parameters<PreviewColumns[number]['render']>[0];

const amountColumn = (title: string, field: string): PreviewColumns[number] => ({
  title,
  render: (item: PreviewItem) => item.file_row?.[field] as number,
});

const getColumns = (): PreviewColumns => [
  { title: 'Name', render: (item) => item.file_row?.commonProperties?.employeeName },
  { title: 'Email Address', render: (item) => item.file_row?.commonProperties?.email },
  {
    title: 'Payroll Month',
    render: (item) => {
      const month = (item.file_row?.payrollMonth as string) || '';
      if (!/^\d{4}-\d{2}$/.test(month)) return month;
      return format(new Date(`${month}-01T00:00:00`), 'MMMM yyyy');
    },
  },
  amountColumn('PF Wages (₹)', 'pfWages'),
  amountColumn('EPS Wages (₹)', 'epsWages'),
  amountColumn('Employer (₹)', 'employerAmount'),
  amountColumn('Employee (₹)', 'employeeAmount'),
  amountColumn('EDLI (₹)', 'edliCharges'),
  amountColumn('Admin (₹)', 'adminCharges'),
  {
    title: 'Warnings',
    render: (item) => ((item.file_row?.warnings as string[] | undefined) ?? []).join('; '),
  },
];

export const bulkPfOverrideConfig: UploadTypeConfigCallback = () => ({
  permissions: [permissions.REPORTS_PF_OVERRIDE],
  initiation: {
    feature: 'pfOverride',
    pageTitle: 'PF Override',
    step1Title: '1. Download the PF Override template',
    step1Subtitle:
      'The template lists every active PF employee. Fill the payroll month and the PF figures for the employees you want to override, and upload it here. Untouched rows are ignored.',
    instructions: [
      'payroll_month is the payroll month as shown on Run Payroll, written as YYYY-MM. Only the September 2026 payroll month (2026-09) can be overridden for now; rows for any other month are rejected.',
      'Amounts are in rupees. Decimals are rounded to the nearest rupee. A blank amount cell counts as 0 once at least one amount in the row is filled; a row with every amount blank is rejected. To zero PF for a month on purpose, type 0 in every amount column — such a row is flagged in the preview.',
      'The template lists every active PF employee. Fill only the rows you want to override; untouched rows are ignored.',
      'One row per employee per payroll month.',
      'admin_charges is EPFO A/C-2 admin charges only; EDLI goes in edli_charges.',
      'edli_charges is 0.5% of EPF wages up to the PF wage ceiling (at most ₹125 on the ₹25,000 ceiling). When epf_wages is above ₹15,000, edli_charges below ₹75 is rejected.',
      'employer_amount is the full employer figure. If your organisation includes PF admin charges in CTC it must already include admin_charges and edli_charges; otherwise it is the employee-matching contribution alone.',
      'Every amount must be greater than 0, unless the whole row is 0 (no PF for that month). Only eps_wages may be 0 on its own.',
      'eps_wages and contributions may exceed epf_wages by at most one rupee (the excess is trimmed); eps_wages cannot exceed the PF wage ceiling for the month.',
      'VPF is not overridden. Employee PF feeds 80C, so TDS and net pay for that month will change.',
      'Months that are already finalized or executed are rejected. Un-finalize first.',
      'Any later salary revision, PF settings change or employee edit recomputes PF and can replace an override. Re-upload after such changes and check the payslip before finalizing.',
      'Do not add or remove columns. There is a limit of 500 filled rows per upload.',
    ],
    onSampleTemplateDownload() {
      window.open('/v2/api/bulk-pf-override/download-template', '_blank');
    },
  },
  preview: {
    feature: 'pfOverride',
    columns: getColumns(),
    renderConfirmationText: () =>
      'Please confirm to apply these PF overrides. Employee PF, TDS and net pay for the listed months will change.',
    successModalCtaText: 'Go to Reports',
    successModalMainText: 'PF overrides have been applied successfully.',
    successModalSubText: () =>
      'The overridden PF figures will be used in the next payroll computation for those months. A later salary, PF settings or employee change can replace them; check the payslip before finalizing.',
    onSuccessModalCtaClick() {
      // Reports is a legacy PHP page, not a React route.
      window.location.assign('/reports/');
    },
    onDownloadErrorReport(history) {
      history?.push({
        pathname: routePaths.bulkUploads.initiate,
        search: `type=${BulkUploadTypes.TYPE_BULK_PF_OVERRIDE}`,
      });
    },
  },
});
